/**
 * Pedido por documento — `tenant.theme_json.storeDocumentOrder`.
 */

/** @param {unknown} themeJson */
export function normalizeThemeJson(themeJson) {
  let obj = themeJson;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  return /** @type {Record<string, unknown>} */ (obj);
}

/**
 * @param {unknown} themeJson
 * @returns {{
 *   enabled: boolean,
 *   aiAvailable: boolean,
 *   openaiConfigured: boolean,
 *   anthropicConfigured: boolean,
 * }}
 */
export function parseStoreDocumentOrder(themeJson) {
  const base = normalizeThemeJson(themeJson);
  const raw = base.storeDocumentOrder;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      enabled: false,
      aiAvailable: false,
      openaiConfigured: false,
      anthropicConfigured: false,
    };
  }
  const openaiConfigured =
    Boolean(raw.openaiConfigured) ||
    (typeof raw.openaiApiKey === 'string' && raw.openaiApiKey.trim() !== '');
  const anthropicConfigured =
    Boolean(raw.anthropicConfigured) ||
    (typeof raw.anthropicApiKey === 'string' && raw.anthropicApiKey.trim() !== '');

  return {
    enabled: Boolean(raw.enabled),
    aiAvailable: openaiConfigured || anthropicConfigured,
    openaiConfigured,
    anthropicConfigured,
  };
}

/**
 * @param {unknown} tenant
 * @returns {boolean}
 */
export function tenantDocumentOrderEnabled(tenant) {
  if (!tenant || typeof tenant !== 'object') return false;
  const flat = tenant.document_order_enabled;
  if (flat === true || flat === 1 || flat === '1') return true;
  return parseStoreDocumentOrder(tenant.theme_json).enabled;
}

/**
 * @param {unknown} tenant
 * @returns {boolean}
 */
export function tenantDocumentOrderAiAvailable(tenant) {
  if (!tenant || typeof tenant !== 'object') return false;
  const flat = tenant.document_order_ai_available;
  if (flat === true || flat === 1 || flat === '1') return true;
  return parseStoreDocumentOrder(tenant.theme_json).aiAvailable;
}

/** @param {unknown} themeJson */
export function storeDocumentOrderBodyForEditor(themeJson) {
  const parsed = parseStoreDocumentOrder(themeJson);
  return {
    enabled: parsed.enabled,
    openaiConfigured: parsed.openaiConfigured,
    anthropicConfigured: parsed.anthropicConfigured,
  };
}

/**
 * Monta bloco para gravar em theme_json (preserva chaves se campos vazios).
 *
 * @param {unknown} prevThemeJson
 * @param {{ enabled: boolean, openaiApiKey: string, anthropicApiKey: string }} input
 */
export function storeDocumentOrderPatchForSave(prevThemeJson, input) {
  const base = normalizeThemeJson(prevThemeJson);
  const prevRaw = base.storeDocumentOrder;
  const prevBlock =
    prevRaw && typeof prevRaw === 'object' && !Array.isArray(prevRaw)
      ? /** @type {Record<string, unknown>} */ (prevRaw)
      : {};

  const next = {
    ...prevBlock,
    enabled: Boolean(input.enabled),
  };

  const openaiTrim = input.openaiApiKey.trim();
  const anthropicTrim = input.anthropicApiKey.trim();
  if (openaiTrim) {
    next.openaiApiKey = openaiTrim;
  } else if (typeof prevBlock.openaiApiKey === 'string' && prevBlock.openaiApiKey.trim() !== '') {
    next.openaiApiKey = prevBlock.openaiApiKey.trim();
  }
  if (anthropicTrim) {
    next.anthropicApiKey = anthropicTrim;
  } else if (typeof prevBlock.anthropicApiKey === 'string' && prevBlock.anthropicApiKey.trim() !== '') {
    next.anthropicApiKey = prevBlock.anthropicApiKey.trim();
  }

  return next;
}
